/* Campaign-only numeric adapter. No requests, review submission or demo scores. */
(() => {
  if (!document.body.classList.contains('template-product-campaign-lab')) return;
  if (window.CampaignReviewRating) return;
  let config = {};
  try { config = JSON.parse(document.getElementById('CampaignReviewRatingConfig')?.textContent || '{}'); } catch (_) { /* Missing data stays missing. */ }
  if (!config || typeof config !== 'object' || Array.isArray(config)) config = {};
  const number = value => {
    if (typeof value !== 'number' && typeof value !== 'string') return null;
    if (typeof value === 'string' && !/^\d+(?:\.\d+)?$/.test(value.trim())) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };
  const score = value => {
    const parsed = number(value);
    return parsed !== null && parsed >= 0 && parsed <= 5 ? parsed : null;
  };
  const count = value => {
    const parsed = number(value);
    return parsed !== null && Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : null;
  };
  const escape = value => String(value).replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
  const format = value => String(Math.round(value * 100) / 100);
  const fills = value => {
    const parsed = score(value);
    return parsed === null ? [] : Array.from({ length: 5 }, (_, index) => Math.round(Math.max(0, Math.min(1, parsed - index)) * 10000) / 100);
  };
  const stars = value => {
    const parsed = score(value);
    if (parsed === null || !config.starUrl) return '';
    const src = escape(config.starUrl);
    return `<span class="campaign-rating-stars" role="img" aria-label="${format(parsed)} out of 5 stars">${fills(parsed).map(fill => `<span class="campaign-rating-stars__slot" aria-hidden="true" style="--star-fill:${fill}%"><img class="campaign-rating-stars__base" src="${src}" width="96" height="96" alt="" decoding="async"><img class="campaign-rating-stars__fill" src="${src}" width="96" height="96" alt="" decoding="async"></span>`).join('')}</span>`;
  };
  const readScore = review => {
    const nodes = [review, ...review.querySelectorAll('[data-score],[aria-label*="out of 5"],img[alt*="out of 5"]')];
    for (const node of nodes) {
      const direct = score(node.getAttribute('data-score'));
      if (direct !== null) return direct;
      for (const attribute of ['aria-label', 'alt']) {
        const match = (node.getAttribute(attribute) || '').match(/(?:^|\s)(\d+(?:\.\d+)?)\s+out of 5(?:\s+stars)?\b/i);
        const parsed = score(match?.[1]);
        if (parsed !== null) return parsed;
      }
    }
    return null;
  };
  const aggregate = bridge => {
    // Prefer a complete widget aggregate when present; otherwise use the official
    // Shopify metafield pair. Never average the first page of loaded review cards.
    const widget = bridge?.querySelector('[data-average-rating][data-number-of-reviews]');
    const widgetScore = score(widget?.getAttribute('data-average-rating'));
    const widgetCount = count(widget?.getAttribute('data-number-of-reviews'));
    if (widgetCount === 0) return { rating: null, count: 0 };
    if (widgetScore !== null && widgetCount > 0) return { rating: widgetScore, count: widgetCount };
    const meta = config.rating;
    const metaScore = score(meta && typeof meta === 'object' ? meta.rating : meta);
    const metaCount = count(config.count);
    const isFivePoint = !meta || typeof meta !== 'object' || (number(meta.scale_max) === 5 && (number(meta.scale_min) === 0 || number(meta.scale_min) === 1));
    const meetsMinimum = !meta || typeof meta !== 'object' || metaScore >= number(meta.scale_min);
    if (metaScore !== null && metaCount > 0 && isFivePoint && meetsMinimum) return { rating: metaScore, count: metaCount };
    return { rating: null, count: widgetCount ?? metaCount };
  };
  const summary = (data, loadedCount = 0) => {
    const total = count(data.count);
    const rating = score(data.rating);
    const label = total !== null ? `${total} ${total === 1 ? 'review' : 'reviews'}` : `${loadedCount} ${loadedCount === 1 ? 'review' : 'reviews'} loaded`;
    if (rating === null || !total) return `<span>${label}</span>`;
    return `<strong>${format(rating)}</strong><div>${stars(rating)}<span>Based on ${label}</span></div>`;
  };
  const inline = data => score(data.rating) === null || !count(data.count) ? '' : `<span class="review-rating-inline">${stars(data.rating)}<span>${format(data.rating)}</span><span>(${data.count})</span></span>`;
  // Product price summary includes the complete public native aggregate plus
  // published product-linked feedback. Never use only the loaded native page.
  const priceAggregate = (native, payload, productId) => {
    const nativeCount = count(native.count) || 0;
    const nativeScore = score(native.rating);
    if (nativeCount && nativeScore === null) return { rating: null, count: 0 };
    const seen = new Set();
    const reviews = String(payload?.productId) === String(productId) && productId && Array.isArray(payload?.reviews)
      ? payload.reviews.filter(review => {
        const value = number(review?.rating);
        if (!review?.id || seen.has(String(review.id)) || !review.name || !review.body || !Number.isInteger(value) || value < 1 || value > 5) return false;
        seen.add(String(review.id));
        return true;
      }) : [];
    const total = nativeCount + reviews.length;
    return {
      rating: total ? ((nativeScore || 0) * nativeCount + reviews.reduce((sum, review) => sum + Number(review.rating), 0)) / total : null,
      count: total
    };
  };
  const refresh = () => {
    const data = aggregate(document.querySelector('[data-judgeme-submission-bridge]'));
    const priceSlot = document.querySelector('[data-price-review-rating]');
    if (priceSlot) {
      const reviewsId = document.querySelector('[data-real-reviews-section]')?.id;
      if (reviewsId) priceSlot.setAttribute('href', `#${reviewsId}`);
      let payload = null;
      try { payload = JSON.parse(document.querySelector('[data-managed-review-data]')?.getAttribute('data-reviews') || 'null'); } catch (_) { /* Invalid feedback is excluded. */ }
      const productId = document.querySelector('#judgeme_product_reviews')?.getAttribute('data-product-id');
      const priceData = priceAggregate(data, payload, productId);
      const markup = priceData.rating === null || !priceData.count ? '' : `<span class="review-rating-inline">${stars(priceData.rating)}<span>${format(priceData.rating)}</span></span>`;
      if (priceSlot.innerHTML !== markup) priceSlot.innerHTML = markup;
      priceSlot.hidden = !markup;
      if (markup) priceSlot.setAttribute('aria-label', `${format(priceData.rating)} out of 5 stars. Read reviews`);
      else priceSlot.removeAttribute('aria-label');
    }
    if (data.rating === null) return;
    const ratingSlot = document.querySelector('[data-real-review-rating]');
    if (ratingSlot) { ratingSlot.innerHTML = inline(data); ratingSlot.hidden = false; }
    const summarySlot = document.querySelector('[data-real-review-summary]');
    if (summarySlot) { summarySlot.className = 'pdp-reviews__score'; summarySlot.innerHTML = summary(data); }
  };
  window.CampaignReviewRating = { score, count, fills, stars, readScore, aggregate, priceAggregate, summary, inline, refresh };
  // Deferred script runs after the DOM is parsed, independently of widget loading.
  refresh();
})();
